import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createServiceApi, CreateServicePayload } from 'TenantModule/api/createServiceApi';
import { integrationQueryKeys } from 'IntegrationModule/api/queries/integrationQueryKeys';
import { serviceQueryKeys } from 'TenantModule/api/queries/serviceQueryKeys';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';

interface CreateServiceErrorResponse {
  error: string;
  message: string;
  statusCode: number;
}

export const useCreateService = () => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    AxiosResponse<unknown>,
    AxiosResponse<CreateServiceErrorResponse>,
    CreateServicePayload
  >({
    mutationKey: ['createService'],
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      return createServiceApi({ params, token });
    },
    onSuccess: () => {
      showSuccess('Service created successfully!');
      queryClient.invalidateQueries({
        queryKey: integrationQueryKeys.list({ identifier: 'useIntegrationDisplayNamesQuery' }),
      });
      // The Register Service To Tenant / Add Service To Realm dropdowns read
      // this catalog (5-minute staleTime); without this the service just
      // created is missing from them until a reload.
      queryClient.invalidateQueries({
        queryKey: serviceQueryKeys.list({ identifier: 'useServicesQuery' }),
      });
    },
    onError: (error) => {
      showError(`Failed to create service: ${error.data?.message ?? 'Unknown error'}`);
    },
  });

  return {
    createService: mutation.mutate,
    isCreatingService: mutation.isPending,
  };
};
